import { useState, useEffect, useCallback, useMemo } from 'react';
import { PROMPT_LIBRARY_TEMPLATES } from '../../../constants/promptLibraryTemplates';

/**
 * Cycles prompt-library prompts through the empty workshop input: each prompt
 * lands fully formed, holds, then rises and fades out before the next one.
 *
 * A native placeholder can't be transformed (CSS transforms don't apply to
 * ::placeholder), so UnlimitedMainView paints the visible copy in an overlay
 * span and keeps the real placeholder transparent — see that file for why the
 * transparent placeholder is still worth setting.
 *
 * Timing lives entirely in the `placeholder-cycle` keyframe (tailwind.config.cjs);
 * this hook just advances an index when the animation reports it has finished,
 * which keeps the two from drifting apart.
 */

// The library prompts are sentence prefixes ("A graphic organizer for"), so they
// trail off to signal the teacher finishes the thought. Display-only — accepting
// fills the bare prefix.
export const promptDisplayText = (template) => `${template.prompt}…`;

/**
 * Frame 0 is whatever placeholder was already on screen; every frame after it is
 * a library prompt, looping. The base copy shows once and is never returned to.
 *
 * @returns {{text: string, template: Object|null}} `template` is what ArrowRight
 *   accepts, and is null on the base frame.
 */
export const frameAt = (basePlaceholder, prompts, index) => {
  if (index === 0) return { text: basePlaceholder, template: null };
  const template = prompts[(index - 1) % prompts.length];
  return { text: promptDisplayText(template), template };
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  );

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const onChange = (e) => setReduced(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
};

/**
 * @param {Object} params
 * @param {string} params.basePlaceholder - Placeholder shown before cycling starts.
 * @param {boolean} params.enabled - False once the input has any content.
 * @param {Object[]} [params.prompts] - The prompt list to cycle; defaults to the
 *   standard library (campaign landing pages pass their variant's templates).
 * @returns {{active: boolean, text: string, activeTemplate: Object|null,
 *   cycleKey: number, advance: Function}} `cycleKey` keys the overlay span so each
 *   prompt remounts and restarts the animation from a fully visible first frame.
 */
export const useCyclingPlaceholder = ({
  basePlaceholder,
  enabled,
  prompts = PROMPT_LIBRARY_TEMPLATES,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const active = enabled && !reducedMotion && prompts.length > 0;
  const [index, setIndex] = useState(0);

  // Rewind whenever the animation is off, so clearing the box starts over from
  // the base copy rather than resuming mid-cycle.
  useEffect(() => {
    if (!active) setIndex(0);
  }, [active]);

  const advance = useCallback(() => setIndex((i) => i + 1), []);
  const { text, template } = frameAt(basePlaceholder, prompts, index);

  return useMemo(
    () => ({
      active,
      text: active ? text : basePlaceholder,
      activeTemplate: active ? template : null,
      cycleKey: index,
      advance,
    }),
    [active, text, template, basePlaceholder, index, advance]
  );
};
